import * as React from "react";
import {
  Button,
  Card,
  DatePicker,
  DatePickerHandle,
  Dropdown,
  GooglePlacesSearch,
  PickerContainer,
  Table,
  Tabs,
  Text,
  Link,
} from "../../components";
import { SVGs } from "../../assets";
import type { DashboardViewModel } from "./viewModel";
import { ExpandedRowDetails } from "./ExpandedRowDetails";
import { TeleHealthBanner } from "./TeleHealthBanner";
import { ProductsParamsSortBy } from "../../apis/products";
import { MemberOptionsSkeleton } from "./MemberOptionsSkeleton";
import { startOfDay } from "date-fns";
import { SystemServiceCode } from "../../stores";
import { Tools } from "../../utils";

export function View({ t, ...props }: DashboardViewModel) {
  const dateRangePopoverRef = React.useRef<DatePickerHandle | null>(null);

  return (
    <div>
      <div className="bg-secondary px-[16px] py-[20px] shadow-soft">
        <div className="grid items-center gap-[20px] md:grid-cols-[repeat(4,minmax(0,1fr))_auto_auto]">
          <GooglePlacesSearch
            label={t("dashboard.filters.chooseCity")}
            placeholder={t("dashboard.searchPlaceholder")}
            value={props?.placeName ?? ""}
            onSelect={(p) => props.setFilter("place", p)}
          />
          <Dropdown
            placeholder="Select service"
            label={t("dashboard.filters.chooseService")}
            value={props.serviceTypeId}
            options={props.serviceOptions}
            onChange={(v) => props.setFilter("serviceTypeId", v)}
          />
          <div className="relative">
            <PickerContainer
              onClick={() => dateRangePopoverRef.current?.open()}
              label={t("dashboard.filters.dateRange")}
              value={Tools.formatDateRange(
                props.dateRange.from,
                props.dateRange.to,
              )}
            />
            <DatePicker
              ref={dateRangePopoverRef}
              minDate={startOfDay(new Date()).toISOString()}
            >
              <DatePicker.Range
                value={props.dateRange}
                onChange={(v) => props.setFilter("dateRange", v)}
              />
            </DatePicker>
          </div>
          <Dropdown
            placeholder="Select a language"
            label={t("dashboard.filters.serviceLanguage")}
            value={props.language}
            options={props.serviceLanguageOptions}
            onChange={(v) => props.setFilter("language", v)}
          />
          <Dropdown
            className="min-w-[150px]"
            label={t("dashboard.filters.predefinedDistance")}
            value={String(props.radius)}
            options={props.radiusOptions}
            onChange={(v) => props.setFilter("radius", v)}
          />
          <Link newPage to={process.env.REACT_APP_MAP_COVERAGE_URL ?? ""}>
            <Button
              children={t("dashboard.mapCoverage")}
              color="secondary"
              className="max-w-[160px]"
              icon={
                <img
                  alt=""
                  className="h-[20px] w-[20px]"
                  src={SVGs.locationMarkerRoundFilled}
                />
              }
            />
          </Link>
        </div>
      </div>
      <Card className="mt-[16px] mb-[30px] mx-[16px]">
        <TeleHealthBanner
          coords={props.coords}
          countryCode={props.countryCode}
          onBook={(p) => props.navigateToBooking(p)}
          emptyText={({ hasTelehealthData }) =>
            props.loading || props.dataSource.length
              ? ""
              : props.coords
                ? hasTelehealthData
                  ? t("dashboard.empty.noInPersonServicesWithTelehealth")
                  : t("dashboard.empty.noInPersonServices")
                : t("dashboard.empty.selectLocation")
          }
        />
        {props.showSkeleton ? (
          <MemberOptionsSkeleton />
        ) : (
          !!props.dataSource.length && (
            <>
              <div className="flex items-center justify-between">
                <Text level={2}>{"Member Option"}</Text>
                <div className="flex items-center gap-[16px]">
                  <Text>{"Ranked by: "}</Text>
                  <Tabs
                    value={props.sortBy}
                    onChange={(v) => props.setFilter("sortBy", v)}
                    items={[
                      {
                        label: t("general.availability"),
                        value: ProductsParamsSortBy.availability,
                      },
                      {
                        label: t("general.price"),
                        value: ProductsParamsSortBy.price,
                      },
                      ...([SystemServiceCode.telehealth].includes(
                        props.service?.code as SystemServiceCode,
                      )
                        ? []
                        : [
                            {
                              label: t("general.distance"),
                              value: ProductsParamsSortBy.distance,
                            },
                          ]),
                    ]}
                  />
                </div>
              </div>
              <Table
                className="mt-[16px]"
                loading={props.loading}
                expandable={{
                  accordion: true,
                  expandedRowRender: (data) => <ExpandedRowDetails {...data} />,
                }}
                columns={props.columns}
                dataSource={props.dataSource}
                rowKey={(row) =>
                  `${row.id}-${row.location ?? row.partner_location?.id ?? ""}`
                }
                pagination={{
                  page: props.page,
                  pageSize: props.pageSize,
                  totalItems: props.totalItems,
                  onLoadMore: props.onLoadMore,
                }}
              />
            </>
          )
        )}
      </Card>
    </div>
  );
}
